import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Badge, Box, Logo, TopNav, type TopNavItem } from '@tempmee/react';
import { NAVBAR_HEIGHT, NAVBAR_Z_INDEX } from '~/constants/layout';
import { logOut, showMessage } from '../../actions';
import history from '../../browserHistory';
import { fireMenuBarAccountClicked, fireMenuBarInboxClicked } from '~/posthogs/events';
import { useInboxCount } from '../Inbox/hooks/useInboxCount';
import { AccountSplitWeb } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { routes } from '~/routes';

interface ITopNavBarProps {
  hasOfficeSelected?: boolean;
}

/**
 * Pins the navbar to the top of the viewport. The v3 TopNav has no `position`
 * prop (the old DS Navbar took `position="fixed"`), so the header is floated in
 * a fixed layer over an in-flow spacer div that reserves its height. The sticky
 * SideBar (top: NAVBAR_HEIGHT) depends on the header staying put here.
 */
const fixedLayerStyle = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  zIndex: NAVBAR_Z_INDEX,
};

export default function TopNavBar({ hasOfficeSelected = true }: ITopNavBarProps) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { totalInbox } = useInboxCount();
  // With the account split on, "Account" becomes the person's own "My profile".
  const isAccountSplitOn = useFeatureFlag(AccountSplitWeb);
  const accountRoute = isAccountSplitOn ? routes.myProfile : routes.account;

  const goToInbox = () => {
    if (location.pathname === '/registration') {
      dispatch(
        showMessage({
          title: 'Error',
          message: 'Please finish your registration to access the rest of the platform.',
          isError: true,
        }),
      );
    } else {
      fireMenuBarInboxClicked();
      history.push('/dashboard/inbox');
    }
  };

  const goToAccount = () => {
    fireMenuBarAccountClicked();
    history.push(accountRoute);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    dispatch(logOut());
  };

  // Map the active nav item from the current route (controlled `value`).
  const activeValue = (() => {
    if (location.pathname === '/dashboard/inbox') return 'inbox';
    if (location.pathname === accountRoute) return 'account';
    return undefined;
  })();

  const handleNavChange = (value: string) => {
    switch (value) {
      case 'inbox':
        goToInbox();
        break;
      case 'account':
        goToAccount();
        break;
      case 'logout':
        handleLogout();
        break;
    }
  };

  const isRegistration = location.pathname.includes('registration');

  const items: TopNavItem[] = (() => {
    if (isRegistration) return [];
    if (!hasOfficeSelected) {
      return [{ value: 'logout', label: 'Log Out', 'data-cy': 'top-nav-logout-menu' }];
    }
    return [
      {
        value: 'inbox',
        label: 'Inbox',
        'data-cy': 'top-nav-inbox-menu',
        badge: totalInbox > 0 ? <Badge severity="flagged" count={totalInbox} /> : undefined,
      },
      {
        value: 'account',
        label: isAccountSplitOn ? 'My profile' : 'Account',
        'data-cy': 'top-nav-account-menu',
      },
    ];
  })();

  return (
    <Box height={`${NAVBAR_HEIGHT}px`}>
      <Box data-testid="top-nav-fixed-layer" style={fixedLayerStyle}>
        <TopNav
          color="dark"
          value={activeValue ?? ''}
          onChange={handleNavChange}
          items={items}
          logo={
            <Logo
              tone="light"
              mark="full"
              height={44}
              style={{ cursor: 'pointer' }}
              onClick={() => history.push('/')}
            />
          }
        />
      </Box>
    </Box>
  );
}
